/**
 * == Controller: KnowledgeEngineController
 *
 * Front-end counterpart to the Knowledge Engine admin view. Persists filter
 * criteria, wires form submissions to Turbo endpoints, and keeps URL search
 * input in sync with cookie-backed state.
 *
 * === Responsibilities:
 * - Enable/disable the filter submit button based on connection state.
 * - Persist `project_id` and `url_search` selections via cookie helpers.
 * - Drive Turbo requests for filter, clear, and live search actions.
 * - Mirror removed filter tags back into the UI when chips are cleared.
 */
import PlatformBaseController from "./platform_base_controller";
import {
    appendActiveOnly,
    resetActiveOnly,
    setActiveOnly,
    visibleActiveOnlyInput,
} from "./mixins/active_only_filter";

// Main controller for handling Knowledge Engine list filtering
export default class extends PlatformBaseController {
    static targets = ["filterSubmit", "projectSelect", "urlSearch"];

    connect() {
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;
    }

    // Handles project select dropdown changes
    selectProject(event) {
        const projectId = event.target.value;
        const params = new URLSearchParams({ id: projectId });
        const activeOnlyInput = visibleActiveOnlyInput();
        appendActiveOnly(params, { root: activeOnlyInput?.closest("form") || document });
        this.setPersistentFilter("project_id", projectId);
        this.fetchAction(`/knowledge_engine/filter_project_knowledge_bases?${params.toString()}`);
    }

    removeFilterTag(event) {
        event.preventDefault();

        const url = new URL(window.location.href);
        const params = url.searchParams;

        const clickedElement = event.currentTarget;
        const filterTag = clickedElement.dataset.filterTagValue;   // e.g. "url_search"
        const projectId = clickedElement.dataset.projectIdValue;

        if (projectId !== undefined) {
            this.setPersistentFilter("project_id", projectId);
            params.set("project_id", projectId);
        }

        if (filterTag) {
            // Client-side best effort (won't work for HttpOnly cookies; server will also delete)
            this.deletePersistentFilter(filterTag);
            this.removeParam(filterTag);
            params.delete(filterTag);
            // Tell server exactly what we removed
            params.set("filterTag", filterTag);
        }

        if (filterTag === "url_search" && this.hasUrlSearchTarget) {
            this.urlSearchTarget.value = "";
        }
        if (filterTag === "active_only") {
            setActiveOnly(false);
            params.set("active_only", "0");
        }

        const activeOnlyInput = visibleActiveOnlyInput();
        appendActiveOnly(params, { root: activeOnlyInput?.closest("form") || document });
        this.fetchAction(`/knowledge_engine/remove_filter_tag?${params.toString()}`);
    }

    // Full filter form submit
    filterProjectKnowledgeBases(event) {
        event.preventDefault();

        const form = event.target.closest("form") || event.target.form || this.element;
        const projectId = this.getProjectId(form);
        const urlSearchInput = form.querySelector('[name="url_search"]');
        const urlSearch = urlSearchInput?.value?.trim() || "";

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        if (urlSearch) {
            this.setPersistentFilter("url_search", urlSearch);
        } else {
            this.deletePersistentFilter("url_search");
        }

        const params = new URLSearchParams(new FormData(form));
        appendActiveOnly(params, { root: form });
        if (!urlSearch) params.delete("url_search");

        this.fetchAction(`/knowledge_engine/filter_project_knowledge_bases?${params.toString()}`);
    }

    setFilters(event) {
        event.preventDefault();
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;

        const form = event.target.closest("form") || event.target.form || this.element;
        const projectId = this.getPersistedFilter(form, "project_id");
        const search = this.getSearch(form);
        const urlSearch = form.querySelector('[name="url_search"]')?.value?.trim();

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        if (urlSearch) {
            this.setPersistentFilter("url_search", urlSearch);
        } else {
            this.deletePersistentFilter("url_search");
            if (this.hasUrlSearchTarget) this.urlSearchTarget.value = "";
        }

        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (projectId) params.append("project_id", projectId);
        if (urlSearch) params.append("url_search", urlSearch);
        appendActiveOnly(params, { root: form });

        this.fetchAction(`/knowledge_engine/set_filters?${params.toString()}`);
    }

    clearFilters(event) {
        event.preventDefault();

        const form = event.target.closest("form") || event.target.form || this.element;
        const clickedElement = event.currentTarget;
        const filterTag = clickedElement.dataset.filterTagValue;
        const projectId = clickedElement.dataset.projectIdValue;
        const search = this.getSearch(form);

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        // Client-side best effort (server will also delete)
        this.deletePersistentFilter("url_search");
        this.removeParam("url_search");
        if (filterTag === "url_search" && this.hasUrlSearchTarget) this.urlSearchTarget.value = "";

        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (projectId) params.set("project_id", projectId);
        resetActiveOnly();
        appendActiveOnly(params, { root: form });

        this.filtersHide(event);
        // ✅ call the correct endpoint so the server clears and deletes cookies
        this.fetchAction(`/knowledge_engine/clear_filters?${params.toString()}`);
    }

    closeSearch(event) {
        event.preventDefault();

        const searchContainer = document.querySelector(".docs_header_search");
        if (searchContainer) {
            searchContainer.style.transition = "opacity .3s";
            searchContainer.style.opacity = 1;
            requestAnimationFrame(() => {
                searchContainer.style.opacity = 0;
                setTimeout(() => {
                    searchContainer.style.display = "none";
                    searchContainer.style.opacity = 1;
                }, 300);
            });
        }

        const form = event.currentTarget.closest("form") || this.element.querySelector("form");
        const searchInput = form?.querySelector('[name="search"]');
        if (searchInput) searchInput.value = "";

        this.removeParam("search");
        this.removeParam("page");

        const projectId = this.getProjectId(form);
        const urlSearchField = document.querySelector('input[name="url_search"]');
        const urlSearch = urlSearchField?.value?.trim();

        if (urlSearch) {
            this.setPersistentFilter("url_search", urlSearch);
        } else {
            this.deletePersistentFilter("url_search");
        }

        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (urlSearch) params.append("url_search", urlSearch);
        const activeOnlyInput = visibleActiveOnlyInput();
        appendActiveOnly(params, { root: activeOnlyInput?.closest("form") || document });

        const query = params.toString();
        this.fetchAction(`/knowledge_engine/filter_project_knowledge_bases${query ? `?${query}` : ""}`);
    }

    // Live search
    search(event) {
        event.preventDefault();

        const form = event.target.closest("form") || event.target.form || this.element;
        const search = form.querySelector('[name="search"]')?.value;
        const projectId = this.getProjectId(form);

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (projectId) params.append("project_id", projectId);
        const activeOnlyInput = visibleActiveOnlyInput();
        appendActiveOnly(params, { root: activeOnlyInput?.closest("form") || document });

        this.fetchAction(`/knowledge_engine/filter_project_knowledge_bases?${params.toString()}`);
    }
}
